import {
    ArrowDropDown,
    ArrowDropUp,
    Edit,
    Email,
    FileDownload,
    NoteAdd,
} from '@mui/icons-material'
import {
    Button,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
} from '@mui/material'
import { type FC, useRef, useState } from 'react'
import { BulkActionContextProvider } from 'recruiter/contexts/BulkActionContext'
import ConfirmDialog from 'shared/components/DocumentUpload/ConfirmDialog'
import AddInternalNotes from '../BulkActions/AddInternalNotes'
import RosterBlaster from '../BulkActions/Blasts/RosterBlaster'
import EditSourceStatus from '../BulkActions/EditSourceStatus'
import EditSystemStatus from '../BulkActions/EditSystemStatus'
import ResendConfirmationEmail from '../BulkActions/ResendConfirmationEmail'
import RosterExportBulkAction from '../BulkActions/RosterExportBulkAction'

interface RosterSearchMoreActionsProps {
    selectedCandidateIds: string[]
    rosterId: string
    canEdit: boolean
    viewMode: 'on' | 'all'
}

export const RosterSearchMoreActions: FC<RosterSearchMoreActionsProps> = ({
    selectedCandidateIds,
    rosterId,
    canEdit,
    viewMode,
}) => {
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
    const [blasterOpen, setBlasterOpen] = useState(false)
    const [internalNotesOpen, setInternalNotesOpen] = useState(false)
    const [sourceStatusOpen, setSourceStatusOpen] = useState(false)
    const [systemStatusOpen, setSystemStatusOpen] = useState(false)
    const [exportMenuOpen, setExportMenuOpen] = useState(false)
    const [modalState, setModalState] = useState({
        message: '',
        onConfirm: () => {},
        open: false,
    })

    const moreActionsRef = useRef<HTMLButtonElement>(null)
    const menuOpen = Boolean(menuAnchor)

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setMenuAnchor(event.currentTarget)
    }

    const handleMenuClose = () => {
        setMenuAnchor(null)
    }

    const buttonSx = {
        textTransform: 'uppercase',
        fontSize: '0.75rem',
        fontWeight: 500,
        py: 0.5,
        px: 1.5,
        borderColor: 'divider',
        color: 'text.secondary',
        '&:hover': {
            borderColor: 'text.secondary',
            bgcolor: 'action.hover',
        },
    }

    const hasCandidates = selectedCandidateIds.length > 0

    return (
        <BulkActionContextProvider>
            <Button
                ref={moreActionsRef}
                variant="outlined"
                size="small"
                onClick={handleMenuOpen}
                sx={buttonSx}
            >
                More Actions
                {menuOpen ? (
                    <ArrowDropUp sx={{ fontSize: '1.25rem', ml: 0.5 }} />
                ) : (
                    <ArrowDropDown sx={{ fontSize: '1.25rem', ml: 0.5 }} />
                )}
            </Button>

            <Menu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleMenuClose}
            >
                {canEdit && (
                    <MenuItem
                        disabled={!hasCandidates}
                        onClick={() => {
                            handleMenuClose()
                            setBlasterOpen(true)
                        }}
                    >
                        <ListItemIcon>
                            <Email fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Blast</ListItemText>
                    </MenuItem>
                )}

                {canEdit && (
                    <MenuItem
                        disabled={!hasCandidates}
                        onClick={() => {
                            handleMenuClose()
                            setInternalNotesOpen(true)
                        }}
                    >
                        <ListItemIcon>
                            <NoteAdd fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Add Internal Notes</ListItemText>
                    </MenuItem>
                )}

                {viewMode === 'on' && (
                    <MenuItem
                        disabled={!hasCandidates}
                        onClick={() => {
                            handleMenuClose()
                            setExportMenuOpen(true)
                        }}
                    >
                        <ListItemIcon>
                            <FileDownload fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Download</ListItemText>
                    </MenuItem>
                )}

                {canEdit && (
                    <ResendConfirmationEmail
                        onClose={handleMenuClose}
                        rosterId={rosterId}
                        candidateIds={selectedCandidateIds}
                        setModalState={setModalState}
                    />
                )}

                <MenuItem
                    disabled={!hasCandidates}
                    onClick={() => {
                        handleMenuClose()
                        setSystemStatusOpen(true)
                    }}
                >
                    <ListItemIcon>
                        <Edit fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Registration Status</ListItemText>
                </MenuItem>

                {canEdit && (
                    <MenuItem
                        disabled={!hasCandidates}
                        onClick={() => {
                            handleMenuClose()
                            setSourceStatusOpen(true)
                        }}
                    >
                        <ListItemIcon>
                            <Edit fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Source Status</ListItemText>
                    </MenuItem>
                )}
            </Menu>

            {blasterOpen && (
                <RosterBlaster
                    onClose={() => setBlasterOpen(false)}
                    isOpen={blasterOpen}
                    candidateIds={selectedCandidateIds}
                    rosterId={rosterId}
                />
            )}

            {internalNotesOpen && (
                <AddInternalNotes
                    onClose={() => setInternalNotesOpen(false)}
                    isOpen={internalNotesOpen}
                    rosterId={rosterId}
                    candidateIds={selectedCandidateIds}
                />
            )}

            {exportMenuOpen && (
                <RosterExportBulkAction
                    anchorRef={moreActionsRef}
                    onClose={() => setExportMenuOpen(false)}
                    isOpen={exportMenuOpen}
                    rosterId={rosterId}
                    candidateIds={selectedCandidateIds}
                />
            )}

            {systemStatusOpen && (
                <EditSystemStatus
                    isOpen={systemStatusOpen}
                    selectedCandidateIds={selectedCandidateIds}
                    anchorRef={moreActionsRef}
                    onClose={() => setSystemStatusOpen(false)}
                />
            )}

            {sourceStatusOpen && (
                <EditSourceStatus
                    rosterId={rosterId}
                    candidateIds={selectedCandidateIds}
                    isOpen={sourceStatusOpen}
                    anchorRef={moreActionsRef}
                    onClose={() => setSourceStatusOpen(false)}
                />
            )}

            <ConfirmDialog
                open={modalState.open}
                handleClose={() => {
                    setModalState({
                        open: false,
                        message: '',
                        onConfirm: () => {},
                    })
                }}
                handleConfirm={modalState.onConfirm}
                message={modalState.message}
            />
        </BulkActionContextProvider>
    )
}
